import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  CheckCircle2Icon,
  CopyIcon,
  IdCardIcon,
  Loader2Icon,
  PlusIcon,
  Trash2Icon,
  TriangleAlertIcon,
} from '@/shared/ui/icons';
import { Button } from '@/shared/ui/buttons/Button';
import { IconButton } from '@/shared/ui/buttons/IconButton';
import { ConfirmDialog } from '@/shared/ui/dialogs/ConfirmDialog';
import { useToast } from '@/shared/hooks/useToast';
import { useMutate } from '@/shared/hooks/useMutate';
import { queryKeys } from '@/shared/api/queryKeys';
import { apiCargoIdDelete, useCargoIds } from '@/entities/cargo-id';

const INVALIDATE = [queryKeys.cargoIds.all, queryKeys.parcels.all];

export const FeaturesCargoIdProfileCargoIdBanner = () => {
  const { t } = useTranslation('cargoId');
  const navigate = useNavigate();
  const toast = useToast();
  const { data, isLoading, isError } = useCargoIds();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const remove = useMutate({
    api: (id: string) => apiCargoIdDelete(id),
    invalidateKeys: INVALIDATE,
    onSuccess: () => {
      toast.success(t('toast.deleted'));
      setDeleteOpen(false);
    },
    onError: (_e, message) => toast.error(message),
  });

  if (isLoading) {
    return (
      <div className="flex items-center gap-3 rounded-2xl border border-brand-100 bg-brand-50 p-4">
        <Loader2Icon className="h-5 w-5 shrink-0 animate-spin text-brand-600" aria-hidden />
        <div className="flex flex-col gap-2">
          <span className="h-3 w-24 animate-pulse rounded bg-brand-100" />
          <span className="h-5 w-40 animate-pulse rounded bg-brand-100" />
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="rounded-2xl border border-slate-200 bg-white p-4">
        <p className="text-xs font-semibold uppercase tracking-wide text-brand-700">
          {t('profileBanner.label')}
        </p>
        <p className="mt-1 text-sm text-ink-muted">{t('loadError')}</p>
      </div>
    );
  }

  // No Cargo ID at all → CTA
  if (!data) {
    return (
      <div className="flex flex-col gap-3 rounded-2xl border border-brand-200 bg-gradient-to-br from-brand-50 to-white p-4 shadow-card">
        <div className="flex items-center gap-2">
          <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand-100 text-brand-700">
            <IdCardIcon className="h-5 w-5" aria-hidden />
          </span>
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-wide text-brand-700">
              {t('profileBanner.label')}
            </p>
            <p className="truncate text-sm font-semibold text-ink">
              {t('profileBanner.noneTitle')}
            </p>
          </div>
        </div>
        <p className="text-xs text-ink-muted">{t('profileBanner.noneHelper')}</p>
        <div className="flex flex-col gap-2 sm:flex-row">
          <Button
            size="sm"
            leftIcon={<PlusIcon className="h-4 w-4" />}
            onClick={() => navigate('/ids/request')}
            className="w-full sm:w-auto"
          >
            {t('profileBanner.getCta')}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => navigate('/ids/connect')}
            className="w-full sm:w-auto"
          >
            {t('profileBanner.connectCta')}
          </Button>
        </div>
      </div>
    );
  }

  const isApproved = data.status === 'APPROVED' && Boolean(data.code);
  const isRejected = data.status === 'REJECTED';

  // REJECTED takes precedence over the pending visual: the user needs a clear
  // signal that they must fix something and resubmit. Bug 1 (2026-06-01): the
  // banner used to render "Tasdiqlanmoqda…" for REJECTED rows.
  if (isRejected) {
    return (
      <div className="flex flex-col gap-3 rounded-2xl border border-red-200 bg-red-50 p-4 shadow-card">
        <div className="flex items-center gap-2">
          <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-red-100 text-red-700">
            <TriangleAlertIcon className="h-5 w-5" aria-hidden />
          </span>
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-wide text-brand-700">
              {t('profileBanner.label')}
            </p>
            <p className="truncate text-sm font-semibold text-ink">
              {t('profileBanner.rejectedTitle')}
            </p>
          </div>
        </div>
        {data.reject_reason ? (
          <div className="rounded-lg border border-red-200 bg-white p-3 text-sm text-ink">
            <p className="text-xs font-semibold uppercase tracking-wide text-red-700">
              {t('rejectReason')}
            </p>
            <p className="mt-1">{data.reject_reason}</p>
          </div>
        ) : (
          <p className="text-xs text-ink-muted">{t('profileBanner.rejectedHelper')}</p>
        )}
        <Button
          size="sm"
          leftIcon={<PlusIcon className="h-4 w-4" />}
          onClick={() => navigate('/ids/request')}
        >
          {t('requestAgain')}
        </Button>
      </div>
    );
  }

  // Pending state (no approved code yet, not rejected)
  if (!isApproved) {
    return (
      <div className="flex flex-col gap-3 rounded-2xl border border-amber-200 bg-amber-50 p-4 shadow-card">
        <div className="flex items-center gap-2">
          <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-amber-100 text-amber-700">
            <IdCardIcon className="h-5 w-5" aria-hidden />
          </span>
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-wide text-brand-700">
              {t('profileBanner.label')}
            </p>
            <p className="truncate text-sm font-semibold text-ink">
              {t('profileBanner.pendingTitle')}
            </p>
          </div>
        </div>
        <p className="text-xs text-ink-muted">{t('profileBanner.pendingHelper')}</p>
        <Button size="sm" variant="outline" onClick={() => navigate('/ids')}>
          {t('profileBanner.viewRequestCta')}
        </Button>
      </div>
    );
  }

  const code = data.code ?? '';

  const onCopy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(code);
      toast.success(t('profileBanner.copied'));
    } catch {
      toast.error(t('profileBanner.copyFailed'));
    }
  };

  // Approved state
  return (
    <div className="flex flex-col gap-3 rounded-2xl border border-brand-300 bg-gradient-to-br from-brand-50 to-white p-4 shadow-card ring-1 ring-brand-200">
      <div className="flex items-center gap-2">
        <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-brand-600 text-white">
          <IdCardIcon className="h-5 w-5" aria-hidden />
        </span>
        <p className="text-xs font-semibold uppercase tracking-wide text-brand-700">
          {t('profileBanner.label')}
        </p>
        <span className="ml-auto inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-xs font-semibold text-emerald-700">
          <CheckCircle2Icon className="h-3.5 w-3.5" aria-hidden />
          {t('profileBanner.trustChip')}
        </span>
      </div>

      <div className="flex items-center gap-2">
        <p className="min-w-0 flex-1 truncate font-mono text-2xl font-bold tracking-wide text-brand-800">
          {code}
        </p>
        <IconButton
          label={t('profileBanner.copy')}
          variant="ghost"
          size="sm"
          onClick={() => void onCopy()}
        >
          <CopyIcon className="h-5 w-5 text-brand-600" />
        </IconButton>
        <IconButton
          label={t('actions.unbind')}
          variant="ghost"
          size="sm"
          disabled={remove.isPending}
          onClick={() => setDeleteOpen(true)}
        >
          <Trash2Icon className="h-5 w-5 text-red-600" />
        </IconButton>
      </div>

      {(data.full_name || data.phone_number) && (
        <div className="flex flex-col gap-0.5 text-sm text-ink">
          {data.full_name && <p className="font-medium">{data.full_name}</p>}
          {data.phone_number && (
            <p className="text-xs text-ink-muted">{data.phone_number}</p>
          )}
        </div>
      )}

      <p className="text-xs text-ink-muted">{t('profileBanner.helper')}</p>

      <ConfirmDialog
        open={deleteOpen}
        title={t('confirmUnbindTitle')}
        description={t('confirmUnbindDescription')}
        confirmLabel={t('actions.unbind')}
        confirmVariant="danger"
        loading={remove.isPending}
        onCancel={() => {
          if (!remove.isPending) setDeleteOpen(false);
        }}
        onConfirm={() => remove.mutate(data.id)}
      />
    </div>
  );
};
